import { useArchiveSlideMutation } from "@api/slides-library";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import {
  DialogBase,
  useSlidesLibraryStore,
} from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { Fragment, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import { handleAsyncOperation } from "@/shared";
import type { TSavedSlide } from "@/shared/types";
import { useGetSavedSlides } from "../hooks";

const DIALOG_ID = "delete-many-slides-dialog";

const SlideDeleteManyDialog = ({ ids }: { ids: string[] }) => {
  const dispatch = useAppDispatch();
  const [archiveSlide, { isLoading }] = useArchiveSlideMutation();
  const { data } = useGetSavedSlides();
  const resetMultiSelectSlides = useSlidesLibraryStore(
    (s) => s.resetMultiSelectSlides,
  );

  const slides = useMemo(() => {
    return data?.filter((slide: TSavedSlide) => ids.includes(slide._id));
  }, [data, ids]);

  const handleClose = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    if (isLoading) return;
    handleAsyncOperation({
      operation: async () => {
        await Promise.all(
          ids.map((id) => archiveSlide({ id }).unwrap()),
        );
        const removedCount = slides?.length ?? ids.length;
        toast.success(
          `${removedCount} ${removedCount === 1 ? "slide" : "slides"} moved to recently deleted`,
        );
        resetMultiSelectSlides();
        handleClose();
      },
      onError: () => {
        toast.error("Failed to remove slides");
      },
    });
  }, [archiveSlide, handleClose, isLoading, resetMultiSelectSlides, slides, ids]);

  return (
    <DialogBase>
      <DialogBase.Header title="Remove slides" onClose={handleClose} />
      <DialogBase.Content className="tw-pt-4">
        <p className="tw-m-0 tw-text-[13px] tw-font-normal tw-text-blue">
          Are you sure that you want to remove following slides: <br />
          <br />
          {slides?.map((slide: TSavedSlide) => (
            <Fragment key={slide._id}>
              <span className="tw-font-semibold">"{slide.title}"</span>
              {slide !== slides[slides.length - 1] && <>, </>}
            </Fragment>
          ))}
        </p>
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          className={cn(isLoading && "tw-cursor-not-allowed")}
          variant="destructive"
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Removing...
            </>
          ) : (
            "Yes, remove"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

SlideDeleteManyDialog.id = DIALOG_ID;
export { SlideDeleteManyDialog };
